import styled from 'styled-components';
import { Checkbox } from 'antd';

const PRIMARY_COLOR = '#6846d1';
const PRIMARY_COLOR_LIGHT = '#8b6dd7';
const PRIMARY_COLOR_LIGHTER = 'rgba(104, 70, 209, 0.1)';
const CONTROL_FONT_SIZE = '0.875rem';
const ICON_FONT_SIZE = '0.75rem';

export const DropdownContainer = styled.div`
  width: ${(props) =>
    typeof props.$width === 'number' ? `${props.$width}px` : props.$width};
  background: #ffffff;
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const SearchBox = styled.div`
  padding: 0.75rem;
  border-bottom: 0.0625rem solid #f0f0f0;

  .ant-input-affix-wrapper {
    border-radius: 0.375rem;

    &:hover {
      border-color: ${PRIMARY_COLOR};
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${PRIMARY_COLOR};
      box-shadow: 0 0 0 0.125rem ${PRIMARY_COLOR_LIGHTER};
    }
  }

  .ant-input-prefix {
    color: ${PRIMARY_COLOR};
  }
`;

export const ParentsList = styled.div`
  max-height: 25rem;
  overflow-y: auto;
  padding: 0.5rem 0;

  /* Custom scrollbar with primary color */
  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: ${PRIMARY_COLOR};
    border-radius: 0.25rem;
    transition: background 0.2s;

    &:hover {
      background: ${PRIMARY_COLOR_LIGHT};
    }
  }
`;

export const ParentItem = styled.div`
  margin-bottom: 0.0625rem;
`;

export const ParentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0.375rem 0.75rem;
  gap: 0.375rem;
  cursor: pointer;
  transition: background 0.2s ease;
  border-radius: 0.25rem;
  margin: 0 0.5rem;
  user-select: none;

  &:hover {
    background: #faf9fe;
  }

  &:active {
    background: #f3f0ff;
  }

  .ant-checkbox-wrapper {
    margin: 0 !important;
    display: flex;
    align-items: flex-start;
    padding-top: 0.125rem;

    .ant-checkbox {
      margin-right: 0;
      flex-shrink: 0;
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${PRIMARY_COLOR};
      border-color: ${PRIMARY_COLOR};
    }

    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
      background-color: ${PRIMARY_COLOR};
    }

    .ant-checkbox:hover .ant-checkbox-inner {
      border-color: ${PRIMARY_COLOR};
    }
  }
`;

export const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  color: rgba(104, 70, 209, 0.75);
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;

  &:hover {
    color: ${PRIMARY_COLOR};
    background: #f3f0ff;
  }

  &:active {
    background: #ece6ff;
  }

  svg {
    font-size: ${ICON_FONT_SIZE};
  }
`;

export const ParentLabel = styled.span`
  font-weight: 400;
  color: #262626;
  font-size: ${CONTROL_FONT_SIZE};
  flex: 1;
  cursor: pointer;
  user-select: none;
  word-break: break-word;
  line-height: 1.4;

  &:hover {
    color: ${PRIMARY_COLOR};
  }
`;

export const ChildrenList = styled.div`
  margin-left: 1.25rem;
  padding-left: 1rem;
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  border-left: 0.125rem solid #e8e8e8;
  max-height: 11.75rem;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  background: ${(props) => (props.$showShadow ? '#fafafa' : 'transparent')};
  border-radius: ${(props) => (props.$showShadow ? '0.25rem' : '0')};
  box-shadow: ${(props) =>
    props.$showShadow
      ? 'inset 0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)'
      : 'none'};

  /* Hide scrollbar by default, show on hover or when scrolling */
  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 0.25rem;
    transition: background 0.2s;
  }

  /* Show scrollbar on hover or when actively scrolling */
  &:hover::-webkit-scrollbar-thumb,
  &:active::-webkit-scrollbar-thumb {
    background: ${PRIMARY_COLOR};

    &:hover {
      background: ${PRIMARY_COLOR_LIGHT};
    }
  }

  /* For Firefox - hide by default */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover {
    scrollbar-color: ${PRIMARY_COLOR} #f0f0f0;
  }
`;

export const ChildItem = styled.div`
  padding: 0.25rem 0.5rem;
  transition: background 0.2s ease;
  border-radius: 0.25rem;
  margin: 0.0625rem 0.25rem 0.0625rem 0;
  display: flex;
  align-items: flex-start;
  min-height: 2rem;
  cursor: pointer;

  &:hover {
    background: #faf9fe;
  }

  &:active {
    background: #f3f0ff;
  }
`;

export const ChildCheckbox = styled(Checkbox)`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0 !important;

  .ant-checkbox {
    top: 0.125rem;
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${PRIMARY_COLOR};
    border-color: ${PRIMARY_COLOR};
  }

  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${PRIMARY_COLOR};
  }

  .ant-checkbox + span {
    padding: 0;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    color: #595959;
    font-size: ${CONTROL_FONT_SIZE};
    line-height: 1.4;
    transition: color 0.2s ease;
    word-break: break-word;
  }

  &:hover .ant-checkbox + span {
    color: #262626;
  }
`;

export const DropdownFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-top: 0.0625rem solid #f0f0f0;
  background-color: #fafafa;

  .ant-btn-primary {
    background-color: ${PRIMARY_COLOR};
    border-color: ${PRIMARY_COLOR};

    &:hover {
      background-color: ${PRIMARY_COLOR_LIGHT};
      border-color: ${PRIMARY_COLOR_LIGHT};
    }
  }
`;

export const SelectAllOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid rgba(104, 70, 209, 0.1);
  background: ${(props) =>
    props.$selected
      ? 'linear-gradient(to right, #f3f0ff, #faf9fe)'
      : 'linear-gradient(to right, #faf9fe, #ffffff)'};
  transition: background 0.2s ease;
  cursor: pointer;

  &:hover {
    background: linear-gradient(to right, #f3f0ff, #faf9fe);
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${PRIMARY_COLOR};
    border-color: ${PRIMARY_COLOR};
  }

  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${PRIMARY_COLOR};
  }

  span {
    font-size: ${CONTROL_FONT_SIZE};
    font-weight: 600;
    color: #6846d1;
  }
`;

export const SelectionSummary = styled.span`
  font-size: ${CONTROL_FONT_SIZE};
  color: ${PRIMARY_COLOR};
  font-weight: 600;
`;

export const ReleaseFilterDropdownContainer = styled(DropdownContainer)`
  box-sizing: border-box;
  max-width: calc(100vw - 48px);
  max-height: 280px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(104, 70, 209, 0.15);

  && .ant-checkbox .ant-checkbox-inner {
    width: 16px;
    height: 16px;
    border: 1px solid #767676;
    border-radius: 3px;
    background-color: #ffffff;
  }

  && .ant-checkbox-checked .ant-checkbox-inner,
  && .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${PRIMARY_COLOR};
    border-color: ${PRIMARY_COLOR};
  }

  && .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    background-color: ${PRIMARY_COLOR};
  }

  && .ant-checkbox:hover .ant-checkbox-inner,
  && .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: ${PRIMARY_COLOR};
  }

  ${SearchBox} {
    padding: 0;
    flex-shrink: 0;
    border: 0;
    background: #fafafa;

    .ant-input-affix-wrapper {
      padding: 10px 12px;
      border: 0;
      border-bottom: 1px solid #e5e7eb;
      border-radius: 0;
      background: #fafafa;
      box-shadow: none;
      font-size: 0.8rem;

      &:hover {
        background: #f5f3ff;
        border-bottom-color: #c4b5fd;
      }

      &:focus-within {
        background: #faf9fe;
        border-bottom: 2px solid #6846d1;
        padding-bottom: 9px;
        box-shadow: none;
      }
    }

    .ant-input {
      font-size: 0.8rem;
      background: transparent;

      &::placeholder {
        color: #9ca3af;
        font-size: 0.78rem;
        font-style: italic;
      }
    }

    .ant-input-clear-icon {
      color: #9ca3af;
      font-size: 0.75rem;
    }
  }

  ${SelectAllOption} {
    flex-shrink: 0;
    padding: 10px 12px;
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;

    span {
      font-size: 0.8rem;
      color: #374151;
    }

    &:hover {
      background: #f5f3ff;
    }
  }

  ${ParentsList} {
    min-height: 0;
    max-height: 220px;
    padding: 4px 0;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, #6846d1 0%, #8b5cf6 100%);
    }
  }

  ${ParentHeader}, ${ChildItem} {
    padding: 8px 12px;
    margin: 2px 6px;
    min-height: 32px;
    border-radius: 6px;

    &:hover {
      background: #f5f3ff;
    }
  }

  ${ParentLabel}, ${ChildCheckbox} .ant-checkbox + span {
    font-size: 0.8rem;
    line-height: 1.4;
    color: #374151;
  }

  ${ChildrenList} {
    background: transparent;
    box-shadow: none;
  }

  ${DropdownFooter} {
    flex-shrink: 0;
    padding: 8px 12px;
    background: #ffffff;
    border-top: 1px solid rgba(104, 70, 209, 0.12);

    .ant-btn {
      font-size: 0.78rem;
    }
  }

  ${SelectionSummary} {
    font-size: 0.78rem;
  }
`;

export const TriggerButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: ${(props) =>
    typeof props.$width === 'number' ? `${props.$width}px` : props.$width};
  min-height: ${(props) => (props.$enhanced ? '2.5rem' : 'unset')};
  padding: ${(props) => (props.$enhanced ? '0.5rem 0.875rem' : '0.5rem 0.75rem')};
  background: #ffffff;
  border: ${(props) =>
    props.$enhanced && (props.$hasSelection || props.$isOpen)
      ? '1px solid rgba(104, 70, 209, 0.2)'
      : '0.0625rem solid #d9d9d9'};
  border-radius: ${(props) => (props.$enhanced ? '0.5rem' : '0.375rem')};
  box-shadow: ${(props) =>
    props.$enhanced && (props.$hasSelection || props.$isOpen)
      ? '0 1px 3px rgba(104, 70, 209, 0.05)'
      : 'none'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: ${CONTROL_FONT_SIZE};
  color: #262626;

  &:hover {
    border-color: ${PRIMARY_COLOR};
    box-shadow: ${(props) =>
      props.$enhanced
        ? '0 2px 6px rgba(104, 70, 209, 0.1)'
        : `0 0 0 0.125rem ${PRIMARY_COLOR_LIGHTER}`};
  }

  &:focus {
    border-color: ${PRIMARY_COLOR};
    box-shadow: ${(props) =>
      props.$enhanced
        ? '0 0 0 3px rgba(104, 70, 209, 0.15)'
        : `0 0 0 0.125rem ${PRIMARY_COLOR_LIGHTER}`};
    outline: none;
  }

  ${(props) =>
    props.$enhanced &&
    props.$isOpen &&
    `
      border-color: ${PRIMARY_COLOR};
      box-shadow: 0 0 0 3px rgba(104, 70, 209, 0.15);
    `}
`;

export const TriggerText = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props) => (props.$isPlaceholder ? '#9ca3af' : '#1f2937')};
  font-weight: ${(props) => (props.$isPlaceholder ? 400 : 500)};
`;

export const TriggerArrow = styled.span`
  margin-left: 0.5rem;
  color: ${PRIMARY_COLOR};
  font-size: ${ICON_FONT_SIZE};
  display: flex;
  align-items: center;
  justify-content: center;
`;
